import { IconButton, Tooltip, useTheme } from "@mui/material";
import { useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";

import { MMS_PICKER_CONSTRAINTS } from "./mmsPickerConstraints";
import {
  MAX_MEDIA_ATTACHMENTS,
  MAX_MEDIA_FILE_SIZE_BYTES,
  mediaSchema,
} from "../../channel/mmsConstraints";
import type { EditorToolbarItemProps } from "../../editor/extensionContract";
import { useMessageDraftStore } from "../../editor/messageDraftStoreFactory";
import type { AssetMessage } from "@/api/connectAssets";
import type { z } from "zod";

import { AssetPickerModal } from "@/features/AssetLibrary/components/AssetPickerModal";
import { formatFileSize } from "@/features/ListsV2/utils/csv";
import { ImageOutlinedIcon } from "@/icons";

export function MediaAttachButton({
  disabled = false,
}: EditorToolbarItemProps) {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { siteId } = useParams({ strict: false });

  const attachMedia = useMessageDraftStore(
    (s) =>
      s.actions.attachMedia as (media: z.infer<typeof mediaSchema>) => void,
  );
  const mediaCount = useMessageDraftStore(
    (s) => s.authoringState.media?.length ?? 0,
  );
  const isAttachMediaDisabled = mediaCount >= MAX_MEDIA_ATTACHMENTS;

  const handleAssetSelect = useCallback(
    (asset: AssetMessage) => {
      const cdnUrl = asset.cdn_url ?? "";
      const parsed = mediaSchema.safeParse({
        name: asset.original_filename,
        mimeType: asset.media_type,
        sizeBytes: asset.size_bytes != null ? Number(asset.size_bytes) : 0,
        cdnUrl,
      });

      if (!parsed.success) {
        const issuePath = parsed.error.issues[0]?.path[0];
        if (issuePath === "mimeType") {
          enqueueSnackbar(MMS_PICKER_CONSTRAINTS.unsupportedTypeMessage, {
            variant: "error",
          });
        } else if (issuePath === "sizeBytes") {
          const sizeBytes =
            asset.size_bytes != null ? Number(asset.size_bytes) : 0;
          enqueueSnackbar(
            `This image is too large (${formatFileSize(sizeBytes)}). Maximum size is ${formatFileSize(MAX_MEDIA_FILE_SIZE_BYTES)}.`,
            { variant: "error" },
          );
        } else {
          enqueueSnackbar("This asset could not be attached.", {
            variant: "error",
          });
        }
        return;
      }

      attachMedia(parsed.data);
    },
    [attachMedia],
  );

  return (
    <>
      <Tooltip title="Upload image or media">
        <span>
          <IconButton
            aria-label="Upload image or media"
            onClick={() => setPickerOpen(true)}
            disabled={disabled || isAttachMediaDisabled}
            sx={{
              color: theme.palette.text.primary,
              width: 24,
              height: 24,
            }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      {siteId && (
        <AssetPickerModal
          open={pickerOpen}
          siteId={siteId}
          constraints={MMS_PICKER_CONSTRAINTS}
          onSelect={handleAssetSelect}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}
